import {Editor, Element, Range, Text, Transforms} from "slate"
import lang from "../../core/lang";

const FORMATS = ['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript', 'code']
const LIST_TYPES = ['numbered-list', 'bulleted-list']
const SECTION_TYPES = ['quote']
const PRE_TYPES = ['code']
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify']

const withInlines = editor => {
    const { insertData, insertText, isInline, isElementReadOnly, isSelectable } =
        editor
    editor.isInline = element =>
        ['link', 'equation'].includes(element.type) || isInline(element)
    editor.isElementReadOnly = element =>
        element.type === 'equation' || isElementReadOnly(element)
    editor.isSelectable = element =>
        element.type !== 'equation' && isSelectable(element)
    editor.insertText = text => {
        if (text && lang.isUrl(text)) {
            wrapLink(editor, text)
        } else {
            insertText(text)
        }
    }
    editor.insertData = data => {
        const text = data.getData('text/plain')
        if (text && lang.isUrl(text)) {
            wrapLink(editor, text)
        } else {
            insertData(data)
        }
    }
    return editor
}

const toggleMark = (editor, format) => {
    const isActive = isMarkActive(editor, format)

    if (isActive) {
        Editor.removeMark(editor, format)
    } else {
        Editor.addMark(editor, format, true)
    }
}

const toggleBlock = (editor, format) => {
    const isActive = isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
    )
    const isList = LIST_TYPES.includes(format)
    const isSection = SECTION_TYPES.includes(format)
    Transforms.unwrapNodes(editor, {
        match: n =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            (LIST_TYPES.includes(n.type) || SECTION_TYPES.includes(n.type)) &&
            !TEXT_ALIGN_TYPES.includes(format),
        split: true,
    })
    let newProperties
    if (TEXT_ALIGN_TYPES.includes(format)) {
        if(isBlockActive(editor, 'paragraph')) {
            newProperties = {
                align: isActive ? undefined : format,
            }
        }
    } else {
        let type
        if(isActive) {
            type = 'paragraph'
        } else {
            if(isList) {
                type = 'list-item'
            } else if(isSection) {
                type = 'paragraph'
            } else {
                type = format
            }
        }
        newProperties = { type }
    }
    Transforms.setNodes(editor, newProperties)
    if (!isActive && (isList || isSection)) {
        const block = { type: format, children: [] }
        Transforms.wrapNodes(editor, block)
    }
}

const insertLink = (editor, url, text, title) => {
    if (editor.selection) {
        wrapLink(editor, url, text, title)
    }
}

const removeLink = editor => {
    if (isLinkActive(editor)) {
        unwrapLink(editor)
    }
}

const isMarkActive = (editor, format) => {
    const marks = Editor.marks(editor)
    return marks ? marks[format] === true : false
}

const isBlockActive = (editor, format, blockType = 'type') => {
    const { selection } = editor
    if (!selection) return false
    const [match] = Array.from(
        Editor.nodes(editor, {
            at: Editor.unhangRange(editor, selection),
            match: n =>
                !Editor.isEditor(n) &&
                Element.isElement(n) &&
                n[blockType] === format,
        })
    )
    return !!match
}

const isPreFormattedActive = (editor) => {
    return PRE_TYPES.some(type => isBlockActive(editor, type))
}

const isLinkActive = editor => {
    return isBlockActive(editor, 'link', 'type')
}

const getSelectedLink = editor => {
    if (!editor.selection) return null;

    const [linkNode] = Editor.nodes(editor, {
        match: n =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            n.type === 'link',
    });

    if (linkNode) {
        const [node] = linkNode;
        const url = node.url; // The link URL
        const text = node.children.map(child => child.text).join(''); // The link text

        return { url, text };
    }

    return null;
}

const getSelectedText = editor => {
    // Check if there's a valid selection
    if (editor.selection) {
        // Use Editor.string to get the text content of the current selection
        return Editor.string(editor, editor.selection);
    }
    return '';
}

function setFontSize(editor, size) {
    const fontSize = getFontSize(editor)
    if(size === 'increase') {
        Editor.addMark(editor, 'size', fontSize + 1)
    } else if (size === 'decrease') {
        Editor.addMark(editor, 'size', fontSize - 1)
    } else {
        const fontSize = Number.parseFloat(size)
        if(!Number.isNaN(fontSize)) {
            Editor.addMark(editor, 'size', fontSize)
        }
    }
}

function getFontSize(editor, defaultFontSize) {
    const marks = Editor.marks(editor)
    return marks?.size ? marks.size : undefined
}

const wrapLink = (editor, url, text, title) => {
    if (isLinkActive(editor)) {
        if(isCollapsed(editor)) {
            selectCurrentNode(editor)
        }
        unwrapLink(editor)
    }

    const link = {
        type: 'link',
        url,
        title,
        children: []
    }
    if (isCollapsed(editor)) {
        link.children.push({ text: lang.isEmpty(text) ? url : text })
        Transforms.insertNodes(editor, link)
    } else {
        if(lang.isNotEmpty(text) && getSelectedText(editor) !== text) {
            Transforms.delete(editor, { at: editor.selection });
            link.children.push({ text: lang.isEmpty(text) ? url : text })
            Transforms.insertNodes(editor, link)
        } else {
            Transforms.wrapNodes(editor, link, { split: true })
        }
        Transforms.collapse(editor, { edge: 'end' })
    }
}

const isCollapsed = editor => {
    const { selection } = editor
    return selection && Range.isCollapsed(selection)
}

const selectCurrentNode = (editor) => {
    const { selection } = editor;

    // Ensure there is a valid, collapsed selection
    if (selection && Range.isCollapsed(selection)) {
        // Get the parent node at the current cursor position
        const [currentNode, path] = Editor.parent(editor, selection.anchor.path);

        // Check if the node is a valid element (optional, based on your requirement)
        if (Element.isElement(currentNode)) {
            // Get the start and end points of the current node
            const start = Editor.start(editor, path);
            const end = Editor.end(editor, path);

            // Set the selection to cover the entire node
            Transforms.select(editor, {
                anchor: start,
                focus: end,
            });

            // Optionally, you can apply formatting or other transformations here
        }
    }
};

const unwrapLink = editor => {
    Transforms.unwrapNodes(editor, {
        match: n =>
            !Editor.isEditor(n) && Element.isElement(n) && n.type === 'link',
    })
}

function clear(editor) {
    const start = Editor.start(editor, [])
    const end = Editor.end(editor, [])
    const range = { anchor: start, focus: end }
    Transforms.delete(editor, {at: range})
}

function reset(editor, nodes) {
    Editor.withoutNormalizing(editor, () => {
        clear(editor)
        Transforms.insertNodes(editor, nodes, {at: [0]})
        Transforms.delete(editor, {at: [editor.children.length - 1]})
        const start = Editor.start(editor, [])
        Transforms.select(editor, start)
    })
}

function escapeHtml(html) {
    const element = document.createElement('div')
    element.innerText = html
    return element.innerHTML
}

function unescapeHtml(escapedHTML) {
    const element = document.createElement('div')
    element.innerHTML = escapedHTML
    return element.innerText
}

function serialize(editor, format = 'json') {
    const node = editor.children
    if(format === 'markdown') {
        return serializeAsMarkdown(node)
    } else if(format === 'html') {
        return serializeAsHTML(node)
    } else {
        return serializeAsJSON(node)
    }
}

function serializeAsMarkdown(node) {
    return ''
}

function serializeAsJSON(node) {
    return JSON.stringify(node)
}

function serializeAsHTML(node) {
    if (Text.isText(node)) {
        let html = escapeHtml(node.text)
        if (node.bold) {
            html = `<b>${html}</b>`
        } else if (node.italic) {
            html = `<i>${html}</i>`
        } else if (node.underline) {
            html = `<u>${html}</u>`
        } else if (node.strikethrough) {
            html = `<s>${html}</s>`
        } else if (node.superscript) {
            html = `<sup>${html}</sup>`
        } else if (node.subscript) {
            html = `<sub>${html}</sub>`
        } else if (node.code) {
            html = `<code>${html}</code>`
        }
        return html
    }

    const children = node.children.map(n => serializeAsHTML(n)).join('')

    switch (node.type) {
        case 'quote':
            return `<blockquote>${children}</blockquote>`
        case 'code':
            return `<code>${children}</code>`
        case 'paragraph':
            return `<p>${children}</p>`
        case 'link':
            return `<a href="${escapeHtml(node.url)}" title="${node.title || node.url}">${children}</a>`
        default:
            return children
    }
}

const RichTextEditor = {
    withInlines,
    toggleMark,
    isMarkActive,
    isBlockActive,
    isLinkActive,
    getSelectedLink,
    getSelectedText,
    toggleBlock,
    insertLink,
    removeLink,
    isPreFormattedActive,
    getFontSize,
    setFontSize,
    serialize,
    clear,
    reset
}

export default RichTextEditor